// lib/axiosInstance.ts
import axios, { AxiosError, AxiosRequestConfig } from 'axios'

const instance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 15000,
  withCredentials: true, // Keep for CSRF cookies if Laravel Sanctum
})

// Add timezone header
instance.interceptors.request.use((config) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  config.headers['X-Timezone'] = timezone;
  return config;
})

// ---------- LocalStorage Helpers ----------
const getToken = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(key)
}

const setToken = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  localStorage.setItem(key, value)
}

const removeToken = (key: string) => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(key)
}

// Redirect to login if needed
const redirectToLogin = () => {
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname
    if (currentPath !== '/') {
      const encodedPath = encodeURIComponent(currentPath)
      window.location.href = `/login?route=${encodedPath}`
    }
  }
}

// ---------- Request Interceptor ----------
instance.interceptors.request.use(
  (config) => {
    const token = getToken('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ---------- Response Interceptor with Refresh ----------
let isRefreshing = false

interface QueueItem {
  resolve: (token: string | null) => void;
  reject: (error: AxiosError) => void;
}

let failedQueue: QueueItem[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error)
    else prom.resolve(token)
  })
  failedQueue = []
}

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // If refresh already in progress, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string | null) => {
              if (token && originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(instance(originalRequest))
            },
            reject,
          })
        })
      }

      // Begin refresh
      isRefreshing = true

      try {
        const refreshUrl = `${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`
        const refreshToken = getToken('refresh_token')

        // Send token in body if available; otherwise backend reads from HttpOnly cookie
        const res = await axios.post(
          refreshUrl,
          refreshToken ? { refresh_token: refreshToken } : {},
          { withCredentials: true }
        )

        const newAccessToken = res.data.access_token
        const newRefreshToken = res.data.refresh_token
        if (!newAccessToken) throw new Error('No access token received')

        setToken('access_token', newAccessToken)
        if (newRefreshToken) setToken('refresh_token', newRefreshToken)

        processQueue(null, newAccessToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        return instance(originalRequest)
      } catch (refreshError) {
        removeToken('access_token')
        removeToken('refresh_token')
        processQueue(refreshError as AxiosError, null)
        redirectToLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default instance
